import type { Category } from './enums';
import { isLive, type LiveProject, type ProjectRecord } from './types';
import { daysBetween } from '../time/date';

/**
 * Project hierarchy, read side (data dictionary §3, "项目分层"). Exactly two levels: a parent
 * project (which can be recorded on directly) and its child projects. Stored data is never
 * rejected for breaking the rules; every reader normalises through these functions.
 */

export type ProjectsById = ReadonlyMap<string, LiveProject>;

/** Index live projects by id; deleted records are left out. */
export function indexProjects(projects: readonly ProjectRecord[]): Map<string, LiveProject> {
  return new Map(projects.filter(isLive).map((project) => [project.id, project]));
}

/**
 * The parent that counts for `project`, or null when it is a top-level project: no `parentId`,
 * a parent that is itself, missing, deleted, or already has a parent (more than two levels).
 * A deactivated parent is still a parent.
 */
export function effectiveParentId(project: LiveProject, byId: ProjectsById): string | null {
  const parentId = project.data.parentId;
  if (typeof parentId !== 'string' || parentId === '' || parentId === project.id) return null;
  const parent = byId.get(parentId);
  if (parent === undefined) return null;
  const grandparentId = parent.data.parentId;
  // The parent has a parent of its own: more than two levels, so this project reads as top-level.
  if (
    typeof grandparentId === 'string' &&
    grandparentId !== '' &&
    grandparentId !== parent.id &&
    byId.has(grandparentId)
  ) {
    return null;
  }
  return parentId;
}

/** Category used for statistics: a child project always takes its parent's category. */
export function effectiveCategory(project: LiveProject, byId: ProjectsById): Category {
  const parentId = effectiveParentId(project, byId);
  const parent = parentId === null ? undefined : byId.get(parentId);
  return (parent ?? project).data.category;
}

/**
 * Stored colour name used for display: a child project with `inheritColor === true` takes its
 * parent's colour, any other project its own (the caller resolves the name with `resolvePaletteColor`).
 */
export function effectiveColor(project: LiveProject, byId: ProjectsById): string {
  if (project.data.inheritColor === true) {
    const parentId = effectiveParentId(project, byId);
    const parent = parentId === null ? undefined : byId.get(parentId);
    if (parent !== undefined) return parent.data.color;
  }
  return project.data.color;
}

function bySortThenId(a: LiveProject, b: LiveProject): number {
  return a.data.sort - b.data.sort || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** The child projects of `parentId` (effective children only), ordered by `sort`. */
export function childrenOf(parentId: string, projects: readonly LiveProject[]): LiveProject[] {
  const byId = new Map(projects.map((project) => [project.id, project]));
  return projects
    .filter((project) => project.id !== parentId && effectiveParentId(project, byId) === parentId)
    .sort(bySortThenId);
}

export interface ProjectTreeNode {
  readonly project: LiveProject;
  /** Empty for a project without children. */
  readonly children: readonly LiveProject[];
}

/** Top-level projects ordered by `sort`, each with its children ordered by `sort`. */
export function projectTree(projects: readonly LiveProject[]): ProjectTreeNode[] {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const childrenByParent = new Map<string, LiveProject[]>();
  const tops: LiveProject[] = [];
  for (const project of projects) {
    const parentId = effectiveParentId(project, byId);
    if (parentId === null) tops.push(project);
    else childrenByParent.set(parentId, [...(childrenByParent.get(parentId) ?? []), project]);
  }
  return tops
    .sort(bySortThenId)
    .map((project) => ({ project, children: (childrenByParent.get(project.id) ?? []).sort(bySortThenId) }));
}

/**
 * True when pickers and quick-mark may offer `project`: it is active and, for a child project,
 * its parent is active too (deactivating a parent hides its children but keeps their data).
 */
export function isProjectSelectable(project: LiveProject, byId: ProjectsById): boolean {
  if (!project.data.active) return false;
  const parentId = effectiveParentId(project, byId);
  const parent = parentId === null ? undefined : byId.get(parentId);
  return parent === undefined || parent.data.active;
}

/**
 * True when `project` is a child project whose latest confirmed block lies `foldDays` or more
 * personal dates before `today` (setting `childFoldDays`): pickers show it under "earlier".
 * Top-level projects never fold, and neither does a child that has never been used
 * (`lastUsedDate` null), so a project created a moment ago is selectable at once.
 */
export function isChildFolded(
  project: LiveProject,
  byId: ProjectsById,
  lastUsedDate: string | null,
  today: string,
  foldDays: number,
): boolean {
  if (effectiveParentId(project, byId) === null) return false;
  if (lastUsedDate === null) return false;
  return daysBetween(lastUsedDate, today) >= foldDays;
}
